<script>
import { mapGetters } from 'vuex';
import { useBranding } from 'shared/composables/useBranding';
import SignupForm from './components/Signup/Form.vue';

export default {
  components: {
    SignupForm,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
  },
};
</script>

<template>
  <main class="relative flex h-screen overflow-hidden new-theme-bg">
    <!-- Grain texture overlay -->
    <div class="grain-overlay" />

    <!-- Left Panel: Branding / Illustration -->
    <section
      class="hidden lg:flex flex-col items-center w-1/2 p-16 pt-64 text-center relative overflow-hidden h-screen z-10 animate-slide-from-left"
    >
      <!-- Vertical Divider Line -->
      <div
        class="absolute right-0 top-1/2 -translate-y-1/2 h-3/4 w-px bg-gradient-to-b from-transparent via-white/50 to-transparent z-20"
      />

      <div
        class="relative z-10 w-full flex flex-col items-center justify-center space-y-8"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-14 drop-shadow-2xl filter brightness-110 animate-float-in"
        />

        <p
          class="text-slate-50 text-xl leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] max-w-md animate-slide-up-delayed"
        >
          {{ $t('LOGIN.BRANDING.TAGLINE') }}<br />
          <span class="text-slate-100 font-normal">
            {{ $t('LOGIN.BRANDING.SUBTITLE') }}
          </span>
        </p>

        <!-- Glowing orb effect -->
        <div class="relative mt-8">
          <div
            class="w-64 h-64 rounded-full bg-gradient-to-tr from-pink-500/20 via-blue-500/30 to-cyan-500/20 blur-3xl opacity-60 animate-pulse-slow"
          />
          <div
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-tr from-pink-400/40 to-blue-400/40 blur-2xl animate-pulse-slower"
          />
        </div>
      </div>
    </section>

    <!-- Right Panel: Signup Card -->
    <section
      class="flex flex-col justify-center items-center w-full lg:w-1/2 px-3 sm:px-4 md:px-6 lg:px-12 py-6 sm:py-8 md:py-12 lg:py-16 relative overflow-hidden h-screen z-10 animate-slide-from-right"
    >
      <!-- Mobile Logo and Brand Name (visible only on mobile, outside the box) -->
      <div
        class="lg:hidden flex flex-col items-center mb-4 sm:mb-6 animate-fade-in-up w-full max-w-sm sm:max-w-md px-4"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-10 sm:h-12 mb-2 sm:mb-3 drop-shadow-2xl filter brightness-110"
        />
        <p
          class="text-slate-50 text-xs sm:text-sm leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] text-center"
        >
          {{ $t('LOGIN.BRANDING.TAGLINE') }}<br />
          <span class="text-slate-100 font-normal">
            {{ $t('LOGIN.BRANDING.SUBTITLE') }}
          </span>
        </p>
      </div>

      <div
        class="w-full max-w-sm sm:max-w-md bg-white/5 backdrop-blur-4xl rounded-xl sm:rounded-2xl md:rounded-3xl border border-white/10 p-4 sm:p-6 md:p-8 lg:p-10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 mx-2 sm:mx-4 md:mx-0 shadow-2xl shadow-black/40"
      >
        <div class="text-center mb-6 sm:mb-8 animate-fade-in-up">
          <h2
            class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 md:mb-4 tracking-wide"
          >
            {{ $t('REGISTER.TRY_WOOT') }}
          </h2>
        </div>

        <div class="animate-fade-in-up-delayed">
          <SignupForm />

          <p class="text-center text-xs sm:text-sm text-gray-300 mt-5">
            {{ $t('REGISTER.HAVE_AN_ACCOUNT') }}
            <router-link
              to="/app/login"
              class="text-purple-400 font-medium hover:text-purple-300 transition-colors"
            >
              {{ replaceInstallationName($t('LOGIN.TITLE')) }}
            </router-link>
          </p>
        </div>
      </div>

      <footer
        class="mt-6 sm:mt-8 lg:mt-10 text-center text-xs text-n-slate-10 px-4 animate-fade-in-delayed"
      >
        {{ $t('LOGIN.BRANDING.COPYRIGHT') }} {{ new Date().getFullYear() }}
        {{ globalConfig.installationName }}
        <span class="text-n-slate-8 hidden sm:inline">
          {{ $t('LOGIN.BRANDING.SEPARATOR') }}
        </span>
        <br class="sm:hidden" />
        <span class="text-n-slate-8">
          {{ $t('LOGIN.BRANDING.FOOTER_TEXT') }}
        </span>
      </footer>
    </section>
  </main>
</template>

<style scoped>
/* Force white text color for form inputs (matches login) */
:deep(.group\/input input),
:deep(input[type='text']),
:deep(input[type='email']),
:deep(input[type='password']) {
  color: white !important;
}

:deep(input::placeholder) {
  color: #9ca3af !important; /* gray-400 */
}

/* Force white color for labels */
:deep(label) {
  color: white !important;
}

/* Terms + links readable on dark card */
:deep(a) {
  color: rgb(192 132 252) !important; /* purple-400 */
}

/* Input surface: translucent glass with purple focus, matching login */
:deep(input[type='text']),
:deep(input[type='email']),
:deep(input[type='password']) {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: transparent !important;
  outline-color: transparent !important;
  border-radius: 0.75rem !important;
}

:deep(input:focus) {
  border-color: rgb(192 132 252 / 0.5) !important;
  outline-color: rgb(192 132 252 / 0.5) !important;
}

/* Gradient submit button, matching login (stays vivid even when disabled) */
:deep(button[type='submit']),
:deep(button[type='submit']:disabled) {
  background-image: linear-gradient(
    to right,
    rgb(124 58 237),
    rgb(139 92 246),
    rgb(124 58 237)
  ) !important;
  color: white !important;
  opacity: 1 !important;
}

/* New theme background - image with grain texture (identical to login) */
.new-theme-bg {
  background: url('/assets/images/auth/login-bg.png') center center / cover
    no-repeat;
  position: relative;
}

.grain-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  background-size: 120px 120px;
  animation: grain 4s steps(10) infinite;
  mix-blend-mode: overlay;
  z-index: 1;
}

@keyframes grain {
  0%,
  100% {
    transform: translate(0, 0);
  }
  10% {
    transform: translate(-2%, -2%);
  }
  20% {
    transform: translate(-1%, 1%);
  }
  30% {
    transform: translate(1%, -1%);
  }
  40% {
    transform: translate(-1%, 1%);
  }
  50% {
    transform: translate(-1%, -1%);
  }
  60% {
    transform: translate(1%, 0%);
  }
  70% {
    transform: translate(0%, 1%);
  }
  80% {
    transform: translate(-1%, -1%);
  }
  90% {
    transform: translate(1%, 1%);
  }
}

@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideFromRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpDelayed {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseSlow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes pulseSlower {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.08);
  }
}

.animate-slide-from-left {
  animation: slideFromLeft 1s ease-out;
}

.animate-slide-from-right {
  animation: slideFromRight 1s ease-out;
}

.animate-float-in {
  animation: floatIn 0.8s ease-out 1s both;
}

.animate-slide-up-delayed {
  animation: slideUpDelayed 0.8s ease-out 1.2s both;
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out 1s both;
}

.animate-fade-in-up-delayed {
  animation: fadeInUp 0.6s ease-out 1.2s both;
}

.animate-fade-in-delayed {
  animation: fadeIn 0.8s ease-out 1.4s both;
}

.animate-pulse-slow {
  animation: pulseSlow 4s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 6s ease-in-out infinite;
}

@media (max-width: 640px) {
  .backdrop-blur-xl {
    backdrop-filter: blur(8px);
  }
}
</style>
